import { useTranslation } from 'react-i18next'
import { useCampaignProgress, type Campaign } from '../../hooks/useCampaigns'
import { SECTION_ICONS } from '../../lib/section-icons'
import { EmptyState } from '../ui/empty-state'
import { Progress } from '../ui/progress'
import { Skeleton } from '../ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'

type Counts = { total: number; todo: number; in_progress: number; done: number }

function pct(c: Counts) {
    return c.total ? Math.round((c.done / c.total) * 100) : 0
}

/** The follow-up: units per status, by site then by building. */
export function CampaignProgressTab({ org, campaign }: { org: string; campaign: Campaign }) {
    const { t } = useTranslation('campaigns')
    const { data, isLoading } = useCampaignProgress(org, campaign.uid, campaign.status !== 'draft')
    if (campaign.status === 'draft') {
        return <EmptyState icon={SECTION_ICONS.campaigns} title={t('units_draft_title')} description={t('units_draft_description')} />
    }
    if (isLoading || !data) return <Skeleton className="h-32 w-full" />
    const row = (key: string, name: string, c: Counts, nested: boolean) => (
        <TableRow key={key} data-testid={`campaign-progress-${name}`}>
            <TableCell className={nested ? 'pl-8' : 'font-medium'}>{name}</TableCell>
            <TableCell className="text-right">{c.todo}</TableCell>
            <TableCell className="text-right">{c.in_progress}</TableCell>
            <TableCell className="text-right">{c.done}</TableCell>
            <TableCell className="w-40">
                <div className="flex items-center gap-2">
                    <Progress value={pct(c)} className="h-2" />
                    <span className="w-10 text-right text-xs text-muted-foreground">{pct(c)} %</span>
                </div>
            </TableCell>
        </TableRow>
    )
    return (
        <div className="space-y-4" data-testid="campaign-progress">
            <div className="grid grid-cols-3 gap-3">
                {(['todo', 'in_progress', 'done'] as const).map((k) => (
                    <div key={k} className="rounded-lg border p-3" data-testid={`campaign-progress-total-${k}`}>
                        <p className="text-2xl font-semibold">{data.totals[k]}</p>
                        <p className="text-xs text-muted-foreground">{t(`unit_status.${k}`)}</p>
                    </div>
                ))}
            </div>
            <div className="overflow-x-auto rounded-lg border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('progress.place')}</TableHead>
                            <TableHead className="text-right">{t('unit_status.todo')}</TableHead>
                            <TableHead className="text-right">{t('unit_status.in_progress')}</TableHead>
                            <TableHead className="text-right">{t('unit_status.done')}</TableHead>
                            <TableHead>{t('progress.done')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {data.sites.flatMap((site) => [
                            row(site.asset?.uid ?? 'none', site.asset?.name ?? t('progress.no_site'), site.counts, false),
                            ...(site.buildings ?? []).map((b) => row(b.asset?.uid ?? b.asset?.name ?? '', b.asset?.name ?? '', b.counts, true)),
                        ])}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
